/**
 * useWebABTestFolders Hook
 *
 * Manages folder operations for Web A/B tests.
 * Provides CRUD operations and folder tree utilities.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { WebPlacementService } from '@modules/firebase-core/services/WebPlacementService';
import { WebABTestFolderService } from '@modules/firebase-core/services/WebABTestFolderService';
import type { WebABTestFolder } from '@modules/firebase-core/converters/webABTestFolderConverter';

/**
 * Options for useWebABTestFolders hook
 */
export interface UseWebABTestFoldersOptions {
  /** Project ID */
  projectId: string;
}

/**
 * Result type for useWebABTestFolders hook
 */
export interface UseWebABTestFoldersResult {
  folders: WebABTestFolder[];
  loading: boolean;
  error: string | null;
  createFolder: (name: string) => Promise<WebABTestFolder>;
  renameFolder: (folderId: string, newName: string) => Promise<void>;
  deleteFolder: (folderId: string) => Promise<void>;
  getFolderContentsCount: (folderId: string) => Promise<number>;
  getFolderOptions: (excludeFolderId?: string) => Array<{ id: string | null; name: string }>;
  refresh: () => Promise<void>;
}

/**
 * Hook for managing Web A/B test folders
 *
 * @param options - Configuration options
 * @returns Folder state and actions
 *
 * @example
 * ```typescript
 * const {
 *   folders,
 *   loading,
 *   createFolder,
 *   renameFolder,
 *   deleteFolder,
 *   getFolderOptions,
 * } = useWebABTestFolders({ projectId: 'project-123' });
 * ```
 */
export function useWebABTestFolders(options: UseWebABTestFoldersOptions): UseWebABTestFoldersResult {
  const { projectId } = options;

  const [folders, setFolders] = useState<WebABTestFolder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Services (created once)
  const folderServiceRef = useRef(new WebABTestFolderService());
  const webPlacementServiceRef = useRef(new WebPlacementService());

  /**
   * Load folders
   */
  const loadFolders = useCallback(async () => {
    if (!projectId) {
      setFolders([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const folderService = folderServiceRef.current;
      const loadedFolders = await folderService.list(projectId);
      setFolders(loadedFolders);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load folders';
      setError(message);
      setFolders([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  /**
   * Create a new folder
   */
  const createFolder = useCallback(async (name: string): Promise<WebABTestFolder> => {
    const folderService = folderServiceRef.current;
    const newFolder = await folderService.create(projectId, { name });

    // Refresh folders list
    await loadFolders();

    return newFolder;
  }, [projectId, loadFolders]);

  /**
   * Rename a folder
   */
  const renameFolder = useCallback(async (folderId: string, newName: string): Promise<void> => {
    const folderService = folderServiceRef.current;
    await folderService.update(projectId, folderId, { name: newName });

    // Refresh folders list
    await loadFolders();
  }, [projectId, loadFolders]);

  /**
   * Delete a folder
   */
  const deleteFolder = useCallback(async (folderId: string): Promise<void> => {
    const folderService = folderServiceRef.current;
    await folderService.delete(projectId, folderId);

    // Refresh folders list
    await loadFolders();
  }, [projectId, loadFolders]);

  /**
   * Get count of A/B tests in a folder
   */
  const getFolderContentsCount = useCallback(async (folderId: string): Promise<number> => {
    const webPlacementService = webPlacementServiceRef.current;
    return await webPlacementService.countABTestsInFolder(projectId, folderId);
  }, [projectId]);

  /**
   * Get folder options for selection (e.g., move dialog)
   * Returns array with Root option first, then all folders except the excluded one
   */
  const getFolderOptions = useCallback((excludeFolderId?: string): Array<{ id: string | null; name: string }> => {
    const options: Array<{ id: string | null; name: string }> = [
      { id: null, name: 'Root (no folder)' },
    ];

    folders
      .filter(folder => folder.id !== excludeFolderId)
      .forEach(folder => {
        options.push({ id: folder.id, name: folder.name });
      });

    return options;
  }, [folders]);

  /**
   * Refresh folders list
   */
  const refresh = useCallback(async () => {
    await loadFolders();
  }, [loadFolders]);

  // Load folders on mount
  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  return {
    folders,
    loading,
    error,
    createFolder,
    renameFolder,
    deleteFolder,
    getFolderContentsCount,
    getFolderOptions,
    refresh,
  };
}
